/**
 * Mark an owned property as sold (disposed).
 *
 * Writes the sale in two places:
 *   1. `property_setting.after_repair_value` ← sale price. Per the product
 *      decision, the sale PRICE overwrites ARV, so the flip/cash-flow math and
 *      the sale-event display immediately reflect the actual close.
 *   2. `pipeline_properties` disposition columns: is_closed, closed_at (the
 *      "YYYY-MM-DD" sale date) and closing_costs (actual sell-side dollars).
 *
 * A single toast covers both writes; the query cache for the Properties page and
 * the property's deal settings is invalidated so the UI reflects the sale.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PostgrestSingleResponse } from "@supabase/supabase-js";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { useWorkspace } from "@/hooks/use-workspace";
import { supabase } from "@/integrations/supabase/client";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

export interface MarkSoldInput {
  /** Sale price / proceeds — overwrites the property's ARV. */
  salePrice: number;
  /** "YYYY-MM-DD" closing date. */
  closedAt: string;
  /** Actual sell-side costs in dollars (commission · transfer · closing). */
  closingCosts: number;
}

type SettingRow = { id: string };

export function useMarkSold(pipelinePropertyId: string) {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  return useMutation({
    mutationFn: async ({ salePrice, closedAt, closingCosts }: MarkSoldInput) => {
      if (!workspaceId) throw new Error("Missing workspace");

      // 1. Overwrite ARV on property_setting (update, or insert when the owned
      //    property somehow has no settings row yet).
      const client = supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (c: string, v: string) => {
              maybeSingle: () => DbResp<SettingRow | null>;
            };
          };
          update: (v: unknown) => { eq: (c: string, v: string) => { select: (s: string) => DbResp<SettingRow[]> } };
          insert: (v: unknown) => DbResp<unknown>;
        };
      };

      const { data: existing, error: findErr } = await client
        .from("property_setting")
        .select("id")
        .eq("pipeline_property_id", pipelinePropertyId)
        .maybeSingle();
      if (findErr) throw findErr;

      if (existing) {
        const { error } = await client
          .from("property_setting")
          .update({ after_repair_value: salePrice })
          .eq("id", existing.id)
          .select("id");
        if (error) throw error;
      } else {
        const { error } = await client.from("property_setting").insert({
          pipeline_property_id: pipelinePropertyId,
          workspace_id: workspaceId,
          created_by: user?.id ?? null,
          after_repair_value: salePrice,
        });
        if (error) throw error;
      }

      // 2. Flag disposition on pipeline_properties.
      const ppClient = supabase as unknown as {
        from: (t: string) => {
          update: (v: unknown) => {
            eq: (c: string, v: string) => { select: (s: string) => DbResp<SettingRow[]> };
          };
        };
      };
      const { data: rows, error } = await ppClient
        .from("pipeline_properties")
        .update({ is_closed: true, closed_at: closedAt, closing_costs: closingCosts })
        .eq("id", pipelinePropertyId)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "mark this property sold");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pipeline_properties_owned"] });
      void queryClient.invalidateQueries({ queryKey: ["deal_settings", pipelinePropertyId] });
      void queryClient.invalidateQueries({ queryKey: ["portfolio"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Property marked as sold");
    },
    onError: (err) => {
      toast.error(toUserMessage(err, "Failed to mark property sold"));
    },
  });
}

type DbResp<T> = Promise<PostgrestSingleResponse<T>>;
